/**
 * Marketer Popup Component
 *
 * Renders marketer popup content
 * following Single Responsibility Principle
 */

import React, {useMemo} from 'react';
import {Popup} from 'react-map-gl';
import {User, Mail, Phone, Building, Clock} from 'lucide-react';
import {Marketer} from '../../../features/marketers/models/marketer.model';
import {MapConfigurationService} from '../../../Services/map/MapConfigurationService';

export interface MarketerPopupProps {
  marketer?: Marketer;
  onClose: () => void;
  className?: string;
}

export const MarketerPopup: React.FC<MarketerPopupProps> = ({
  marketer,
  onClose,
  className = ''
}) => {
  const mapConfig = MapConfigurationService.getInstance();

  const formatRelativeTime = (date: Date | undefined) => {
    if (!date) {
      return 'غير محدد';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 60) {
      return `منذ ${diffMinutes} دقيقة`;
    } else if (diffHours < 24) {
      return `منذ ${diffHours} ساعة`;
    } else {
      return `منذ ${diffDays} يوم`;
    }
  };

  const popup = useMemo(() => {
    if (!marketer) {
      return null;
    }

    // Validate coordinates for popup
    const isValidCoordinates = mapConfig.validateCoordinates(
      marketer.latitude,
      marketer.longitude
    );

    if (!isValidCoordinates) {
      return null;
    }

    return (
      <Popup
        longitude={marketer.longitude}
        latitude={marketer.latitude}
        anchor="top"
        onClose={onClose}
        className={`marketer-popup ${className}`}
        maxWidth="300px"
      >
        <div className="p-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center border-2 border-gray-100">
              <User className="w-6 h-6 text-brand-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">
                {marketer.displayName}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                @{marketer.username}
              </p>

              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${marketer.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="text-gray-700">{marketer.statusText}</span>
                </div>
                {marketer.onlineStatus !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${marketer.onlineStatus ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    <span className="text-gray-700">{marketer.onlineStatus ? 'متصل' : 'غير متصل'}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-700">{marketer.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-700">{marketer.phoneNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-700">مطور تسويقي</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-700">{formatRelativeTime(marketer.lastLocationUpdate)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  }, [marketer, onClose, className, mapConfig]);

  return popup;
};

export default MarketerPopup;
